import React from "react";
import { useQuery } from "@tanstack/react-query";

import { Activity, AlertTriangle, Clock, Database, Refresh } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { fetchHealthStatus } from "@/lib/api";
import {
  componentSeverity,
  energyWriterSeverity,
  evaluateEnergyHydration,
  evaluateEnergyWriter,
  num,
  overallSeverity,
  severityOfIssues,
  writerAbsenceNote,
  type HealthIssue,
  type HealthStatus,
  type Severity,
} from "@/lib/health";

const REFRESH_MS = 12_000;

/* ------------------------------------------------------------------ */
/*  Formatting                                                         */
/* ------------------------------------------------------------------ */

/** "—" rather than 0 for absent numbers: a missing counter is not a zero one. */
function fmtNum(v: unknown, digits = 0): string {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function fmtBool(v: unknown): string {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "—";
}

function fmtSeconds(v: unknown): string {
  const n = num(v);
  if (n === null) return "—";
  if (n < 60) return `${Math.round(n)}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
  if (n < 86400) return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
  return `${Math.floor(n / 86400)}d ${Math.floor((n % 86400) / 3600)}h`;
}

function fmtMs(v: unknown): string {
  const n = num(v);
  if (n === null) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n.toFixed(n < 10 ? 1 : 0)}ms`;
}

function fmtClock(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleTimeString();
}

/* ------------------------------------------------------------------ */
/*  Severity presentation                                              */
/* ------------------------------------------------------------------ */

const SEVERITY_BADGE: Record<Severity, { label: string; variant: "success" | "warning" | "error" | "neutral" }> = {
  ok: { label: "Healthy", variant: "success" },
  warning: { label: "Degraded", variant: "warning" },
  critical: { label: "Critical", variant: "error" },
  off: { label: "Disabled", variant: "neutral" },
};

const DOT_CLASS: Record<Severity, string> = {
  ok: "bg-emerald-400",
  warning: "bg-amber-400",
  critical: "bg-red-500",
  off: "bg-muted-foreground/50",
};

function SeverityDot({ level }: { level: Severity }) {
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${DOT_CLASS[level]}`} />;
}

function SeverityBadge({ level }: { level: Severity }) {
  const s = SEVERITY_BADGE[level];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function IssueList({ issues }: { issues: HealthIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {issues.map((issue, i) => (
        <li
          key={i}
          className={`flex items-start gap-2 rounded-md border p-2 text-xs ${
            issue.level === "CRITICAL"
              ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"
              : "border-amber-400/40 bg-amber-400/10 text-amber-600 dark:text-amber-400"
          }`}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-semibold">{issue.level}</span> — {issue.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One label/value cell of a dense metric grid. */
function Metric({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: Severity }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[11px] uppercase tracking-wide opacity-55" title={label}>
        {label}
      </div>
      <div
        className={`font-mono text-sm ${
          tone === "critical"
            ? "text-red-600 dark:text-red-400"
            : tone === "warning"
              ? "text-amber-600 dark:text-amber-400"
              : ""
        }`}
      >
        {value}
      </div>
      {hint && <div className="truncate text-[11px] opacity-50" title={hint}>{hint}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component table                                                    */
/* ------------------------------------------------------------------ */

interface ComponentRow {
  key: string;
  label: string;
  level: Severity;
  detail: string;
  reason?: string | null;
}

function buildComponentRows(s: HealthStatus): ComponentRow[] {
  const writerIssues = evaluateEnergyWriter(s.energy_writer, s.energy_hydration);
  const hydrationIssues = evaluateEnergyHydration(s.energy_hydration, s.energy_writer);
  const heartbeat = (age: unknown) => (num(age) === null ? "no heartbeat" : `heartbeat ${fmtSeconds(age)} ago`);

  return [
    {
      key: "core",
      label: "Core (web / orchestrator)",
      level: componentSeverity(s.core),
      detail: `pid ${s.core?.pid ?? "—"} · up ${fmtSeconds(s.core?.uptime_s)}`,
      reason: s.core?.reason,
    },
    {
      key: "redis",
      label: "Redis",
      level: componentSeverity(s.redis),
      detail: num(s.redis?.latency_ms) === null ? "—" : `ping ${fmtMs(s.redis?.latency_ms)}`,
      reason: s.redis?.reason,
    },
    {
      key: "postgres",
      label: "PostgreSQL",
      level: componentSeverity(s.postgres),
      detail: num(s.postgres?.latency_ms) === null ? "—" : `SELECT 1 ${fmtMs(s.postgres?.latency_ms)}`,
      reason: s.postgres?.reason,
    },
    {
      key: "udp_listener",
      label: "UDP listener",
      level: componentSeverity(s.udp_listener),
      detail: [
        heartbeat(s.udp_listener?.last_heartbeat_age_s),
        s.udp_listener?.hostname ? `on ${s.udp_listener.hostname}` : null,
        s.udp_listener?.missing_components?.length
          ? `missing: ${s.udp_listener.missing_components.join(", ")}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      reason: s.udp_listener?.reason,
    },
    {
      key: "cmd_channel_listener",
      label: "Command channel listener",
      level: componentSeverity(s.cmd_channel_listener),
      detail: heartbeat(s.cmd_channel_listener?.last_heartbeat_age_s),
      reason: s.cmd_channel_listener?.reason,
    },
    {
      key: "diagnostics_logs",
      label: "Diagnostics logs mirror",
      level: componentSeverity(s.diagnostics_logs),
      detail: `last push ${fmtSeconds(s.diagnostics_logs?.last_push_age_s)} ago`,
      reason: s.diagnostics_logs?.reason,
    },
    {
      key: "diagnostics_metrics",
      label: "Diagnostics metrics mirror",
      level: componentSeverity(s.diagnostics_metrics),
      detail: [
        `snapshot ${fmtSeconds(s.diagnostics_metrics?.snapshot_age_s)} ago`,
        num(s.diagnostics_metrics?.ttl_remaining_s) === null
          ? null
          : `ttl ${fmtSeconds(s.diagnostics_metrics?.ttl_remaining_s)}`,
      ]
        .filter(Boolean)
        .join(" · "),
      reason: s.diagnostics_metrics?.reason,
    },
    {
      key: "energy_writer",
      label: "Energy checkpoint writer",
      level: energyWriterSeverity(s.energy_writer, s.energy_hydration),
      detail:
        s.energy_writer?.running === false
          ? s.energy_writer?.reason || "not running"
          : `${fmtNum(s.energy_writer?.dirty_backlog)} dirty · oldest ${fmtMs(s.energy_writer?.oldest_dirty_age_ms)}`,
      reason: writerIssues[0]?.message,
    },
    {
      key: "energy_hydration",
      label: "Energy hydration (PG → Redis)",
      level: severityOfIssues(hydrationIssues),
      detail:
        s.energy_hydration?.completed === true
          ? `completed ${fmtClock(s.energy_hydration?.completed_at)} · ${fmtNum(s.energy_hydration?.rows_hydrated)} rows`
          : s.energy_hydration?.reason || "not completed",
      reason: hydrationIssues[0]?.message,
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function SystemHealthPage() {
  const healthQ = useQuery({
    queryKey: ["health", "status"],
    queryFn: fetchHealthStatus,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  const data = healthQ.data;
  const overall = data ? overallSeverity(data) : "warning";
  const writer = data?.energy_writer;
  const hydration = data?.energy_hydration;
  const writerIssues = data ? evaluateEnergyWriter(writer, hydration) : [];
  const hydrationIssues = data ? evaluateEnergyHydration(hydration, writer) : [];
  const writerLevel = data ? energyWriterSeverity(writer, hydration) : "warning";
  const absenceNote = data ? writerAbsenceNote(writer, hydration) : null;
  const rows = data ? buildComponentRows(data) : [];

  const oldestMs = num(writer?.oldest_dirty_age_ms);
  const rps = num(writer?.rows_per_second);
  const capacity = num(writer?.capacity_rows_per_second);
  const throughputTone: Severity | undefined =
    rps !== null && capacity !== null && capacity > 0 && rps < capacity * 0.5 ? "warning" : undefined;
  const oldestTone: Severity | undefined =
    oldestMs === null ? undefined : oldestMs > 300_000 ? "critical" : oldestMs > 120_000 ? "warning" : undefined;

  return (
    <div className="space-y-4 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">System Health</h1>
            {data && <SeverityBadge level={overall} />}
          </div>
          <div className="text-sm opacity-70">
            Live <span className="font-mono">/health/status</span> — per-component probes, heartbeats
            and the energy checkpoint writer.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-xs opacity-60">
            <div className="flex items-center justify-end gap-1">
              <Clock className="h-3.5 w-3.5" />
              {healthQ.isFetching ? "refreshing…" : `updated ${fmtClock(healthQ.dataUpdatedAt ? new Date(healthQ.dataUpdatedAt).toISOString() : null)}`}
            </div>
            <div>auto every {REFRESH_MS / 1000}s · server {fmtClock(data?.checked_at)}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => healthQ.refetch()} disabled={healthQ.isFetching}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {healthQ.isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load /health/status: {(healthQ.error as Error)?.message || "unknown error"}
        </div>
      )}

      {!data && !healthQ.isError && <div className="text-sm opacity-60">Loading health status…</div>}

      {data && (
        <>
          {/* --- Energy writer: the reason this page exists ------------- */}
          <Card>
            <CardContent className="space-y-4 py-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4" /> Energy checkpoint writer
                </CardTitle>
                <div className="flex items-center gap-2">
                  <SeverityBadge level={writerLevel} />
                  <span className="text-xs opacity-60">
                    {writer?.running === false ? "not running" : `heartbeat ${fmtSeconds(writer?.heartbeat_age_s)} ago`}
                  </span>
                </div>
              </div>

              <IssueList issues={writerIssues} />

              {/* The disabled-vs-stalled verdict is inferred from the hydration
                  marker, not read from config — say so rather than let the badge
                  imply the backend reported it. */}
              {absenceNote && (
                <div className="rounded-md border border-border bg-muted/40 p-2 text-xs opacity-70">
                  {absenceNote}
                  {writer?.reason ? ` (${writer.reason})` : ""}
                </div>
              )}

              {/* Queue lag. Backlog size alone is expected to be large on a
                  100k fleet — oldest_dirty_age_ms is the real lag signal. */}
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">Queue &amp; lag</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric
                    label="Oldest dirty age"
                    value={fmtMs(writer?.oldest_dirty_age_ms)}
                    tone={oldestTone}
                    hint="the real lag signal"
                  />
                  <Metric
                    label="Dirty backlog"
                    value={fmtNum(writer?.dirty_backlog)}
                    hint="size alone is not a fault"
                  />
                  <Metric label="Claimed in flight" value={fmtNum(writer?.claimed_in_flight)} />
                  <Metric
                    label="Projected drain"
                    value={fmtSeconds(writer?.projected_drain_seconds)}
                    hint="backlog at current rate"
                  />
                  <Metric
                    label="Target freshness"
                    value={fmtSeconds(writer?.target_freshness_seconds)}
                    hint={
                      num(writer?.reference_fleet_units) === null
                        ? undefined
                        : `for ${fmtNum(writer?.reference_fleet_units)} units`
                    }
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">Throughput &amp; ticks</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label="Rows / second" value={fmtNum(writer?.rows_per_second, 1)} tone={throughputTone} />
                  <Metric
                    label="Capacity rows / s"
                    value={fmtNum(writer?.capacity_rows_per_second, 1)}
                    hint={
                      num(writer?.interval_seconds) === null
                        ? undefined
                        : `tick every ${fmtSeconds(writer?.interval_seconds)}`
                    }
                  />
                  <Metric label="Tick duration" value={fmtMs(writer?.tick_duration_ms)} />
                  <Metric label="Tick p99" value={fmtMs(writer?.tick_ms_p99)} />
                  <Metric
                    label="Tick overran interval"
                    value={fmtBool(writer?.tick_overran_interval)}
                    tone={writer?.tick_overran_interval ? "warning" : undefined}
                  />
                  <Metric
                    label="Ticks overran"
                    value={fmtNum(writer?.ticks_overran)}
                    tone={(num(writer?.ticks_overran) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label="Ticks" value={fmtNum(writer?.ticks)} />
                  <Metric
                    label="Ticks failed"
                    value={fmtNum(writer?.ticks_failed)}
                    tone={(num(writer?.ticks_failed) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label="Batch p50 / p99" value={`${fmtMs(writer?.batch_ms_p50)} / ${fmtMs(writer?.batch_ms_p99)}`} />
                  <Metric label="Batch max" value={fmtMs(writer?.batch_ms_max)} />
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">Rows &amp; faults</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label="Rows written" value={fmtNum(writer?.rows_written)} />
                  <Metric
                    label="Rows rejected"
                    value={fmtNum(writer?.rows_rejected)}
                    tone={(num(writer?.rows_rejected) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label="Remarked advanced" value={fmtNum(writer?.remarked_advanced)} />
                  <Metric
                    label="Released on failure"
                    value={fmtNum(writer?.released_on_failure)}
                    tone={(num(writer?.released_on_failure) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric
                    label="Missing snapshots"
                    value={fmtNum(writer?.missing_snapshots)}
                    tone={(num(writer?.missing_snapshots) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric
                    label="Corrupt snapshots"
                    value={fmtNum(writer?.corrupt_snapshots)}
                    tone={(num(writer?.corrupt_snapshots) ?? 0) > 0 ? "warning" : undefined}
                  />
                  <Metric label="Reclaimed expired leases" value={fmtNum(writer?.reclaimed_expired_leases)} />
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide opacity-55">Redis compatibility</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
                  <Metric label="Running" value={fmtBool(writer?.running)} />
                  <Metric label="Redis version" value={writer?.redis_version || "—"} />
                  <Metric
                    label="Supports ZADD LT"
                    value={fmtBool(writer?.redis_supports_zadd_lt)}
                    tone={writer?.redis_supports_zadd_lt === false ? "critical" : undefined}
                    hint="requires Redis >= 6.2"
                  />
                  <Metric label="Version checked" value={fmtBool(writer?.redis_version_checked)} />
                  <Metric label="Heartbeat age" value={fmtSeconds(writer?.heartbeat_age_s)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* --- Hydration -------------------------------------------- */}
          <Card>
            <CardContent className="space-y-3 py-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Database className="h-4 w-4" /> Energy hydration (PostgreSQL → Redis)
                </CardTitle>
                <SeverityBadge level={severityOfIssues(hydrationIssues)} />
              </div>

              <IssueList issues={hydrationIssues} />

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
                <Metric label="Completed" value={fmtBool(hydration?.completed)} />
                <Metric label="Started at" value={fmtClock(hydration?.started_at)} />
                <Metric label="Completed at" value={fmtClock(hydration?.completed_at)} />
                <Metric label="Rows scanned" value={fmtNum(hydration?.rows_scanned)} />
                <Metric label="Rows hydrated" value={fmtNum(hydration?.rows_hydrated)} />
                <Metric label="Rows repaired" value={fmtNum(hydration?.rows_repaired)} />
                <Metric label="Rows skipped (lower)" value={fmtNum(hydration?.rows_skipped_lower)} />
                {/* Only rendered when the deployment reports them: an absent
                    counter means "not reported", never zero. */}
                {num(hydration?.rows_failed) !== null && (
                  <Metric
                    label="Rows failed"
                    value={fmtNum(hydration?.rows_failed)}
                    tone={(num(hydration?.rows_failed) ?? 0) > 0 ? "critical" : undefined}
                  />
                )}
                {num(hydration?.redis_round_trips) !== null && (
                  <Metric label="Redis round trips" value={fmtNum(hydration?.redis_round_trips)} />
                )}
              </div>

              {hydration?.reason && !hydration?.completed && (
                <div className="text-xs opacity-60">{hydration.reason}</div>
              )}
            </CardContent>
          </Card>

          {/* --- All components --------------------------------------- */}
          <Card>
            <CardContent className="py-5">
              <CardTitle className="mb-3 text-sm">Components</CardTitle>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide opacity-55">
                      <th className="py-2 pr-3 font-medium">Component</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key} className="border-b border-border/60 last:border-0 align-top">
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <SeverityDot level={row.level} />
                            <span>{row.label}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <SeverityBadge level={row.level} />
                        </td>
                        <td className="py-2 font-mono text-xs opacity-75">
                          <div>{row.detail || "—"}</div>
                          {row.reason && row.level !== "ok" && (
                            <div className="mt-0.5 font-sans opacity-70">{row.reason}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
